import { Color, Radius } from '@openfin/ui-library';
import { createMemoryHistory, MemoryHistory } from 'history';
import { WindowOption } from 'openfin/_v2/api/window/windowOption';
import ReactDOM from 'react-dom';
import { v4 } from 'uuid';
import { ServiceStore } from '../store/ServiceStore';
import { FrameApp, FrameAppProps } from '../view/containers/FrameApp/FrameApp';
import { renderComponentInWindow } from '../view/utils/RenderApp';
import { WebWindow, WebWindowFactory } from './WebWindow';

const windowOptions: WindowOption = {
  name: '', // Replaced when frame is created
  url: 'frame.html',
  autoShow: true,
  minHeight: 200,
  defaultHeight: 900,
  defaultWidth: 344,
  maxWidth: 344,
  maximizable: false,
  resizable: true,
  shadow: true,
  saveWindowState: false,
  contextMenu: !(process.env.NODE_ENV === 'production'),
  frame: false,
  alwaysOnTop: true,
  showTaskbarIcon: false,
  opacity: 0,
  cornerRounding: {
    height: Number(Radius.small.replace('px', '')),
    width: Number(Radius.small.replace('px', '')),
  },
  resizeRegion: {
    sides: {
      top: true,
      right: false,
      bottom: true,
      left: false,
    },
  },
  backgroundColor: Color.darkGray3,
};

export class Frame {
  public webWindow: Promise<WebWindow>;
  public readonly frameId: string;
  private readonly _history: MemoryHistory<unknown> = createMemoryHistory();

  constructor(
    private readonly _webWindowFactory: WebWindowFactory,
    private readonly _store: ServiceStore,
    public readonly id: string,
    public readonly isStream: boolean,
    private readonly onDestroy: () => void
  ) {
    this.frameId = v4();

    this.webWindow = this._webWindowFactory
      .createWebWindow({ ...windowOptions, name: this.frameId, defaultCentered: true })
      .then((webWindow) => {
        const domTarget = webWindow.document.getElementById('react-app');
        if (!domTarget) {
          throw new Error('Unable to create Frame, no dom target');
        }
        const props: FrameAppProps = {
          id: this.id,
          isStream: this.isStream,
          onClose: () => this.destroy(),
          store: this._store,
          history: this._history,
          onReady: async () => {
            await webWindow.setAsForeground();
            await webWindow.animate(
              {
                opacity: {
                  opacity: 1,
                  duration: 100,
                },
              },
              { interrupt: false, tween: 'ease-in' }
            );
          },
        };
        renderComponentInWindow(FrameApp, webWindow, this._store, domTarget, props);
        return webWindow;
      });
  }

  public async destroy(): Promise<void> {
    const webWindow = await this.webWindow;
    const domTarget = webWindow.document.getElementById('react-app');
    if (domTarget) {
      ReactDOM.unmountComponentAtNode(domTarget);
    } else {
      console.error(
        `Unable to unmount frame ${this.frameId}:${this.id}, DOM target not found. Closing window...`
      );
    }
    webWindow.close();
    this.onDestroy();
  }
}
